import React, { useState, useEffect } from 'react';
import FilterComponent from '../Filters/Filters';
import ActionsComponent from '../Actions/Actions';
import TableComponent from '../BulkEditTable/BulkEditTable';
import moment from 'moment';
import * as _ from 'lodash-es';
const styles = require('./styles.pcss');

interface Filter {
  name: string;
  label: string;
  type: string;
  options?: string[];
  config?: {[key: string]: any};
}

interface DataRow {
  id?: number;
  isSelected?: boolean;
  [key: string]: any;
}

interface TransactionsListProps {
  transactions: DataRow[];
  organizationId: string | undefined;
  refreshTransactions: Function;
  discardTransactions: Function;
  restoreTransactions: Function;
}

const TransactionsList: React.FC<TransactionsListProps> = (props) => {
  const [data, setData] = useState<DataRow[]>(props.transactions);
  const [filteredData, setFilteredData] = useState<DataRow[]>(props.transactions);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const getInitialStartDate = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    return new Date(year, month, 1).toISOString().slice(0, 10);
  };
  const [filterValues, setFilterValues] = useState<Record<string, string>>({ startDate: getInitialStartDate() });
  const [hasDiscardedTransactions, setHasDiscardedTransactions] = useState<boolean>(false);
  const [prevDates, setPrevDates] = useState<{ startDate?: string; endDate?: string }>({ startDate: getInitialStartDate() });

  const filters: Filter[] = [
    { name: 'search', label: 'Search', type: 'search' },
    {
      name: 'postingDate',
      label: 'Posting Date',
      type: 'date',
      config: {
        startDate: filterValues.startDate ? moment(filterValues.startDate) : null,
        endDate: filterValues.endDate ? moment(filterValues.endDate) : null,
      },
    },
  ];

  const formatData = (data: DataRow[]) => {
    return data.map(({ amount, merchant, postingDate, ccNumber, externalId, id, status, disabled = false }) => ({
      postingDate: new Date(postingDate).toLocaleDateString('en-US', { timeZone: 'UTC' }),
      amount: amount.toFixed(2),
      merchant,
      ccNumber: ccNumber.slice(-4),
      externalId,
      id,
      status,
      isSelected: false,
      disabled,
    }));
  };

  useEffect(() => {
    props.refreshTransactions(props.organizationId, getInitialStartDate());
  }, []);

  useEffect(() => {
    setData(props.transactions);
    setFilteredData(props.transactions);
  }, [props.transactions]);

  useEffect(() => {
    const { startDate, endDate } = filterValues;
    if ((startDate && startDate !== prevDates.startDate) || (endDate && endDate !== prevDates.endDate)) {
      props.refreshTransactions(props.organizationId, startDate.slice(0, 10), endDate.slice(0, 10));
      setPrevDates({ startDate, endDate });
    }
    const applyFilters = () => {
      const filtered = formatData(
        data.filter((row) => {
          return Object.keys(filterValues).every((filterName) => {
            if (filterValues[filterName]) {
              if (filterName === 'search') {
                return searchTable(row, filterName);
              } else if (!filterName.toLowerCase().includes('date')) {
                return matchFilter(row, filterName);
              }
            }
            return true;
          });
        })
      );
      data.some((row) => row.status === 'discarded') && setHasDiscardedTransactions(true);
      filtered.sort((a, b) => new Date(b.postingDate).getTime() - new Date(a.postingDate).getTime());
      setFilteredData(filtered);
      setTotalPages(Math.ceil(filtered.length / 50));
    };
    applyFilters();
  }, [filterValues, data]);

  const searchTable = (row: Record<string, string>, filterName: string) => {
    return Object.values(row).some((value) =>
      value.toString().toLowerCase().includes(filterValues[filterName].toLowerCase())
    );
  };

  const matchFilter = (row: Record<string, string>, filterName: string) => {
    return row[filterName]
      .toString()
      .toLowerCase()
      .includes(filterValues[filterName].toLowerCase());
  };

  const handleFilterChange = (newFilterValues: Record<string, string>) => {
    if (Object.keys(newFilterValues).some((filterName) => filterName.toLowerCase().includes('date'))) {
      const startDate = newFilterValues.startDate && moment(newFilterValues.startDate).toDate();
      const endDate = newFilterValues.endDate && moment(newFilterValues.endDate).toDate();
      newFilterValues = {
        ...newFilterValues,
        startDate: startDate instanceof Date ? startDate.toISOString() : '',
        endDate: endDate instanceof Date ? endDate.toISOString() : '',
      };
    }
    setFilterValues((prevValues) => ({ ...prevValues, ...newFilterValues }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectRow = (id: number) => {
    setSelectedIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevSelectedIds, id];
      }
    });

    setFilteredData((prevFilteredData) =>
      prevFilteredData.map((row) =>
        row.id === id ? { ...row, isSelected: !row.isSelected } : row
      )
    );
  };

  const handleDiscardClick = async (selectedIds: number[]) => {
    await props.discardTransactions(props.organizationId, selectedIds);
    setFilteredData((prevFilteredData) =>
      prevFilteredData.map((row) => {
        if (selectedIds.includes(row.id!)) {
          return { ...row, status: 'processing', isSelected: false, disabled: true };
        }
        return row;
      })
    );
    setSelectedIds([]);
  };

  const handleRestoreClick = async (selectedIds: number[]) => {
    await props.restoreTransactions(props.organizationId, selectedIds);
    setFilteredData((prevFilteredData) => {
      const updatedData = prevFilteredData.map((row) => {
        if (selectedIds.includes(row.id!)) {
          return { ...row, status: 'processing', isSelected: false, disabled: true };
        }
        return row;
      })
      return updatedData;
    });
    setSelectedIds([]);
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * 50,
    currentPage * 50,
  );

  const selectedRows = filteredData.filter((row) => selectedIds.includes(row.id!));
  const hasPostedRows = selectedRows.some((row) => row.status === 'posted');
  const hasDiscardedRows = selectedRows.some((row) => row.status === 'discarded');
  const hasMixedStatuses = new Set(selectedRows.map((row) => row.status)).size > 1;

  const isDiscardDisabled = selectedIds.length === 0 || !hasPostedRows || hasMixedStatuses;
  const isRestoreDisabled = selectedIds.length === 0 || !hasDiscardedRows || hasMixedStatuses;

  const headers = Object.keys(paginatedData[0] || {}).filter((key) => key !== 'id' && key !== 'isSelected' && key !== 'disabled');

  return (
    <div>
      <div className={styles.controlsContainer}>
        <div className={styles.filtersWrapper}>
          <FilterComponent filters={filters} onFilterChange={handleFilterChange} />
        </div>
        <div className={styles.actionsContainer}>
          <ActionsComponent
            selectedIds={selectedIds}
            onActionClick={handleDiscardClick}
            label="Discard Transactions"
            color="red"
            isDisabled={isDiscardDisabled}
          />
          <ActionsComponent
            selectedIds={selectedIds}
            onActionClick={handleRestoreClick}
            label="Restore Transactions"
            isDisabled={isRestoreDisabled}
          />
        </div>
      </div>
      <TableComponent
        data={paginatedData}
        headers={headers}
        currentPage={currentPage}
        totalPages={totalPages}
        onClickNextPage={() => handlePageChange(currentPage + 1)}
        onClickPreviousPage={() => handlePageChange(currentPage - 1)}
        onSelectRow={handleSelectRow}
      />
    </div>
  );
};

export default TransactionsList;
